import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import WizardShell from "./WizardShell";
import VoiceTextarea from "@/components/VoiceTextarea";
import SpeakButton from "@/components/SpeakButton";
import { sendKata } from "@/lib/api";

export default function KataStep({
  attention,
  witness,
  contextText,
  refinedProblem,
  history,
  setHistory,
  onBack,
  onNext,
}) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const scrollRef = useRef(null);
  const didOpenRef = useRef(false);

  // Open the conversation if there's no history yet
  useEffect(() => {
    if (history.length === 0 && !didOpenRef.current) {
      didOpenRef.current = true;
      openConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, sending, opening]);

  const openConversation = async () => {
    setOpening(true);
    try {
      const res = await sendKata({
        refined_problem: refinedProblem,
        attention,
        witness,
        context: contextText || undefined,
        history: [],
        user_message: null,
      });
      setHistory([{ role: "assistant", content: res.reply }]);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Something went wrong.";
      toast.error("Couldn't reach your Trusted Peer", { description: msg });
    } finally {
      setOpening(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    const newHistory = [...history, { role: "user", content: text }];
    setHistory(newHistory);
    setDraft("");
    setSending(true);

    try {
      const res = await sendKata({
        refined_problem: refinedProblem,
        attention,
        witness,
        context: contextText || undefined,
        history: newHistory,
        user_message: text,
      });
      setHistory([...newHistory, { role: "assistant", content: res.reply }]);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Something went wrong.";
      toast.error("Couldn't reach your Trusted Peer", { description: msg });
      // Roll back the user message so they can retry
      setHistory(history);
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const canContinue = history.length >= 2 && !sending;

  return (
    <WizardShell
      pillLabel="Step 4 · Kata"
      pillColor="#e93e36"
      headline="Spar with the idea before you build it."
      aiOpening={null}
      testid="kata-step"
      footer={
        <>
          <button
            type="button"
            className="weave-btn-outline"
            onClick={onBack}
            data-testid="kata-back-btn"
          >
            ← Back
          </button>
          <button
            type="button"
            className="weave-btn-primary"
            onClick={onNext}
            disabled={!canContinue}
            data-testid="kata-next-btn"
          >
            Continue to Evolve
          </button>
        </>
      }
    >
      <div className="weave-card-soft mb-6" data-testid="kata-problem-recall">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-1">
          The problem you chose
        </div>
        <p className="text-slate-700 italic">"{refinedProblem}"</p>
      </div>

      <div
        ref={scrollRef}
        className="space-y-4 max-h-[480px] overflow-y-auto pr-1"
        data-testid="kata-history"
      >
        {opening && (
          <div className="flex items-center gap-3 text-slate-500">
            <span className="weave-spinner" />
            Your Trusted Peer is thinking…
          </div>
        )}
        {history.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={
              m.role === "assistant"
                ? "weave-bubble-ai"
                : "weave-bubble-user ml-8 md:ml-16"
            }
            data-testid={`kata-msg-${i}`}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                {m.role === "assistant" ? "Trusted Peer" : "You"}
              </div>
              {m.role === "assistant" && (
                <SpeakButton
                  text={m.content}
                  label="Read this reply aloud"
                  testid={`kata-msg-speak-${i}`}
                />
              )}
            </div>
            <div className="text-slate-700 whitespace-pre-line leading-relaxed">
              {m.content}
            </div>
          </motion.div>
        ))}
        {sending && (
          <div className="flex items-center gap-3 text-slate-500">
            <span className="weave-spinner" />
            Listening, then replying…
          </div>
        )}
      </div>

      <div className="mt-6">
        <VoiceTextarea
          rows={3}
          placeholder="Push back, agree, or ask them something. ⌘ + Enter to send."
          value={draft}
          onChange={setDraft}
          onKeyDown={onKey}
          ariaLabel="Type or speak your reply"
          testid="kata-input"
          micTestid="kata-input-mic"
          disabled={sending || opening}
        />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Aim for at least one exchange before moving on.
          </span>
          <button
            type="button"
            className="weave-btn-primary"
            onClick={send}
            disabled={!draft.trim() || sending || opening}
            data-testid="kata-send-btn"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </WizardShell>
  );
}
